// src/pages/finance-portal/contractor-payments/ContractorPayments.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, Tabs, Tab, CircularProgress,
    InputAdornment, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';
import ContractorPaymentForm from './ContractorPaymentForm';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#f1f5f9', color: '#64748b' },
    APPROVED: { bg: '#dbeafe', color: '#1e40af' },
    PAID: { bg: '#dcfce7', color: '#166534' },
    VOID: { bg: '#f3f4f6', color: '#6b7280' },
};

const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Paid', value: 'PAID' },
    { label: 'Void', value: 'VOID' },
];

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

export default function ContractorPayments() {
    const navigate = useNavigate();
    const [payments, setPayments] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');

    // Dialogs
    const [formOpen, setFormOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [ytdOpen, setYtdOpen] = useState(false);
    const [ytdData, setYtdData] = useState([]);
    const [ytdLoading, setYtdLoading] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchPayments = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (statusFilter) params.status = statusFilter;
            if (search) params.search = search;
            const res = await axiosInstance.get('/api/finance/payroll/contractor-payments', { params });
            if (res.data.success) {
                setPayments(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load contractor payments');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, statusFilter, search]);

    useEffect(() => { fetchPayments(); }, [fetchPayments]);

    const handleApprove = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/payroll/contractor-payments/${id}/approve`);
            setSuccess('Payment approved');
            fetchPayments();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to approve payment');
        }
    };

    const handleVoid = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/payroll/contractor-payments/${id}/void`);
            setSuccess('Payment voided');
            fetchPayments();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to void payment');
        }
    };

    const handleDelete = async () => {
        if (!deleteDialog) return;
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/payroll/contractor-payments/${deleteDialog}`);
            setDeleteDialog(null);
            setSuccess('Payment deleted');
            fetchPayments();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete payment');
        } finally {
            setDeleting(false);
        }
    };

    const handleYtdSummary = async () => {
        setYtdOpen(true);
        setYtdLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/payroll/contractor-payments/ytd-summary');
            if (res.data.success) {
                setYtdData(res.data.data || []);
            }
        } catch (err) {
            console.error('Failed to load YTD summary', err);
        } finally {
            setYtdLoading(false);
        }
    };

    const tabIndex = STATUS_TABS.findIndex(t => t.value === statusFilter);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PaymentsIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Contractor Payments</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Manage 1099 contractor payments</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<SummarizeIcon />}
                            onClick={handleYtdSummary}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                borderColor: 'rgba(255,255,255,0.5)', color: 'white',
                                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                            }}
                        >
                            YTD Summary
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => { setEditData(null); setFormOpen(true); }}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            }}
                        >
                            New Payment
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Status Tabs */}
                <Tabs
                    value={tabIndex >= 0 ? tabIndex : 0}
                    onChange={(_, idx) => { setStatusFilter(STATUS_TABS[idx].value); setPage(0); }}
                    variant="scrollable" scrollButtons="auto"
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', color: '#64748b', minHeight: 40 },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    {STATUS_TABS.map(t => <Tab key={t.value} label={t.label} />)}
                </Tabs>

                {/* Search */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search payments..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        sx={{ minWidth: { xs: '100%', sm: 280 } }}
                    />
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Payment #', 'Contractor', 'Date', 'Amount', 'Method', 'Invoice #', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            {[1, 2, 3, 4, 5, 6, 7, 8].map(j => (
                                                <TableCell key={j}><Skeleton height={36} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : payments.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No contractor payments found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : payments.map(p => (
                                    <TableRow key={p.id} sx={{ '&:hover': { bgcolor: '#f8fafc' }, cursor: 'pointer' }}>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: ACCENT }}>
                                            {p.payment_number || `CP-${p.id}`}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{p.contractor_name || '—'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatDate(p.payment_date)}</TableCell>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{formatCurrency(p.amount)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{p.payment_method || '—'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{p.invoice_number || '—'}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Chip
                                                label={getStatusLabel(p.status)}
                                                size="small"
                                                sx={{
                                                    fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                    bgcolor: (STATUS_COLORS[p.status] || STATUS_COLORS.DRAFT).bg,
                                                    color: (STATUS_COLORS[p.status] || STATUS_COLORS.DRAFT).color,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {p.status === 'DRAFT' && (
                                                    <>
                                                        <Tooltip title="Approve">
                                                            <IconButton size="small" onClick={() => handleApprove(p.id)}>
                                                                <CheckCircleIcon sx={{ fontSize: 18, color: ACCENT }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" onClick={() => setDeleteDialog(p.id)}>
                                                                <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                {(p.status === 'APPROVED' || p.status === 'PAID') && (
                                                    <Tooltip title="Void">
                                                        <IconButton size="small" onClick={() => handleVoid(p.id)}>
                                                            <BlockIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]}
                        sx={{ '& *': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Payment Form Dialog */}
            <ContractorPaymentForm
                open={formOpen}
                onClose={() => { setFormOpen(false); setEditData(null); }}
                onSaved={() => { fetchPayments(); setSuccess('Payment saved'); }}
                editData={editData}
            />

            {/* YTD Summary Dialog */}
            <Dialog open={ytdOpen} onClose={() => setYtdOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Contractor YTD Summary</DialogTitle>
                <DialogContent>
                    {ytdLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: ACCENT }} /></Box>
                    ) : ytdData.length === 0 ? (
                        <Typography sx={{ fontFamily, color: '#94a3b8', textAlign: 'center', py: 4 }}>No data</Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Contractor', 'Total Paid', 'Payment Count', 'Needs 1099'].map(h => (
                                            <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ytdData.map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell sx={bodyCellSx}>{row.contractor_name || '—'}</TableCell>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{formatCurrency(row.total_paid)}</TableCell>
                                            <TableCell sx={bodyCellSx}>{row.payment_count || 0}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip
                                                    label={(row.total_paid || 0) >= 600 ? 'Yes' : 'No'}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                        bgcolor: (row.total_paid || 0) >= 600 ? '#fef3c7' : '#f1f5f9',
                                                        color: (row.total_paid || 0) >= 600 ? '#92400e' : '#64748b',
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setYtdOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Payment?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog(null)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
                        {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
